import type { OnApproveData } from '@paypal/paypal-js';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { saveBraintreePaymentMethod, setupBraintreePaymentMethod } from '#src/app/shared/api/payments';
import type { WithTokenParams } from '#src/app/shared/types';
import type { PaymentMethodSaveOptions, PaymentType, PaypalCheckout } from '#src/app/shared/utils/payments';

type OnApproveParams = {
	email: string;
	firstName?: string;
	lastName?: string;
	onApproveData: OnApproveData;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	paypalCheckout: PaypalCheckout | null;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	withToken: (params: WithTokenParams) => void | Promise<void>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
	savePaypalTemporarily?: boolean;
};

// eslint-disable-next-line max-lines-per-function
export async function onApprove(
	{
		email,
		firstName,
		lastName,
		onApproveData,
		onTokenError,
		paypalCheckout,
		paymentMethodSaveOptions,
		withToken,
		prePaymentChecks,
		savePaypalTemporarily,
	}: OnApproveParams,
	utilityContext: UtilityContext,
): Promise<void> {
	if (!paypalCheckout) {
		return Promise.reject(new Error('No paypal checkout instance'));
	}

	const { nonce } = await paypalCheckout.tokenizePayment(
		Object.assign(onApproveData, {
			billingToken: String(onApproveData.billingToken),
			payerId: String(onApproveData.payerID),
		}),
	);

	if (prePaymentChecks && !(await prePaymentChecks('paypal'))) {
		return Promise.resolve();
	}

	if (paymentMethodSaveOptions.shouldSavePaymentMethod) {
		try {
			await saveBraintreePaymentMethod({
				firstName,
				lastName,
				token: nonce,
				email,
				usage: paymentMethodSaveOptions.usage,
				utilityContext,
				paymentType: 'PAYPAL',
			});
		} catch (error) {
			return onTokenError('paypal', error as Error);
		}
	}

	if (savePaypalTemporarily && !paymentMethodSaveOptions.shouldSavePaymentMethod) {
		try {
			await setupBraintreePaymentMethod({
				token: nonce,
				usage: 'OFF_SESSION',
				utilityContext,
				paymentType: 'PAYPAL',
				firstName,
				lastName,
			});
		} catch (error) {
			return onTokenError('paypal', error as Error);
		}
	}

	void withToken({ paymentType: 'paypal', token: nonce });
	return Promise.resolve();
}
